// ===================================================================
// Settings — Activity Log Viewer
// Shows recent system activity from the activityLog table
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Text, Paper, Table, TextInput, Select, Group, Badge, Loader, Center, Stack,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { fetchTable, TABLES } from '../../lib/api-switch';
import { fmtDateTime } from '../../lib/dateFormat';
import { useAuthStore } from '../../store/authStore';

interface LogEntry {
  id: number;
  incidentId: string;
  module: string;
  action: string;
  actorName: string;
  actorEmail: string;
  targetType: string;
  targetId: string;
  details: string;
  datecreated: string;
}

const ACTION_COLORS: Record<string, string> = {
  create: 'green', update: 'blue', delete: 'red',
  approve: 'teal', deny: 'orange', submit: 'violet',
  login: 'gray', export: 'cyan',
};

export default function ActivityLogTab() {
  const { token } = useAuthStore();
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const rows = await fetchTable<LogEntry>(TABLES.activityLog, { token: token ?? undefined, limit: 500 });
      setEntries(rows.sort((a, b) => (b.datecreated || '').localeCompare(a.datecreated || '')));
    } catch { /* */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const modules = useMemo(() => {
    const set = new Set(entries.map(e => e.module).filter(Boolean));
    return [...set].sort().map(m => ({ value: m, label: m }));
  }, [entries]);

  const filtered = useMemo(() => {
    let list = entries;
    if (moduleFilter) list = list.filter(e => e.module === moduleFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(e =>
        (e.actorName || '').toLowerCase().includes(q) ||
        (e.actorEmail || '').toLowerCase().includes(q) ||
        (e.action || '').toLowerCase().includes(q) ||
        (e.details || '').toLowerCase().includes(q) ||
        (e.targetType || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [entries, moduleFilter, search]);

  if (loading) return <Center p="xl"><Loader /></Center>;

  return (
    <Stack gap="md">
      <Group gap="sm">
        <TextInput
          placeholder="Search activity..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.currentTarget.value)}
          size="sm"
          style={{ flex: 1, maxWidth: 300 }}
        />
        <Select
          data={[{ value: '', label: 'All Modules' }, ...modules]}
          value={moduleFilter || ''}
          onChange={v => setModuleFilter(v || null)}
          size="sm"
          w={180}
        />
        <Badge variant="light" size="lg">{filtered.length} entries</Badge>
      </Group>

      <Paper withBorder style={{ maxHeight: 500, overflow: 'auto' }}>
        <Table striped highlightOnHover stickyHeader fz="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>When</Table.Th>
              <Table.Th>Who</Table.Th>
              <Table.Th>Module</Table.Th>
              <Table.Th>Action</Table.Th>
              <Table.Th>Target</Table.Th>
              <Table.Th>Details</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text size="sm" c="dimmed" ta="center" py="lg">No activity logged yet.</Text>
                </Table.Td>
              </Table.Tr>
            )}
            {filtered.slice(0, 200).map(e => (
              <Table.Tr key={e.id}>
                <Table.Td><Text size="xs" c="dimmed">{fmtDateTime(e.datecreated)}</Text></Table.Td>
                <Table.Td>
                  <Text size="xs" fw={500}>{e.actorName || e.actorEmail || '—'}</Text>
                </Table.Td>
                <Table.Td><Badge size="xs" variant="light">{e.module || '—'}</Badge></Table.Td>
                <Table.Td>
                  <Badge size="xs" color={ACTION_COLORS[e.action] || 'gray'} variant="light">
                    {e.action || '—'}
                  </Badge>
                </Table.Td>
                <Table.Td><Text size="xs">{e.targetType || '—'}</Text></Table.Td>
                <Table.Td><Text size="xs" c="dimmed" lineClamp={1}>{e.details || '—'}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
    </Stack>
  );
}
